import { useEffect } from 'react';
import { updatePageSEO } from '../../utils/seo';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function PrivacyPolicy() {
  useEffect(() => {
    updatePageSEO({
      title: 'Privacy Policy - Market Me | Your Data Protection & Privacy Rights',
      description: 'Learn how Market Me collects, uses, and protects your personal information. Our privacy policy explains your data rights, cookie usage, and our commitment to GDPR compliance.',
      keywords: 'privacy policy, data protection, GDPR, personal information, Market Me privacy',
      canonical: 'https://marketme.com/privacy-policy'
    });
  }, []);

  return (
    <div className="relative min-h-screen bg-black text-white">
      {/* Fixed Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-black"></div>
        <div className="absolute inset-0 opacity-100">
          <div 
            className="absolute inset-0 animate-flow-slow"
            style={{
              background: 'radial-gradient(circle at 20% 30%, rgba(139, 92, 246, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(59, 130, 246, 0.12) 0%, transparent 50%)',
            }}
          ></div>
        </div>
      </div>

      <div className="relative z-10">
        <Navigation />
        
        <div className="max-w-[900px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-white/60 mb-8 text-sm md:text-base">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

          <div className="space-y-8 text-white/80 leading-relaxed">
            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">1. Introduction</h2>
              <p className="mb-4">
                Market Me ("we", "our", or "us") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, store, and protect your information in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
              </p>
              <p>
                By using our website and services, you consent to the data practices described in this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">2. Data Controller</h2>
              <p className="mb-2">Market Me is the data controller responsible for your personal data.</p>
              <p className="mb-2"><strong>Contact details:</strong></p>
              <p>Email: team.marketme@gmail.com</p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">3. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-white mb-3 mt-6">3.1 Information You Provide Directly</h3>
              <p className="mb-4">We collect personal information that you voluntarily provide when you:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Contact Forms:</strong> Name, email address, phone number (optional), and message content</li>
                <li><strong>Free Audit Requests:</strong> Name, email address, website URL, and business type</li>
                <li><strong>Chat Widget:</strong> Name, email address, and conversation content</li>
                <li><strong>Service Enquiries:</strong> Business information, project requirements, and communication preferences</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">3.2 Information Collected Automatically</h3>
              <p className="mb-4">When you visit our website, we automatically collect certain information:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Analytics Data:</strong> IP address, browser type, device information, pages visited, time spent on pages, referring website</li>
                <li><strong>Cookies:</strong> Session identifiers, preference settings, and tracking data (see our Cookie Policy for details)</li>
                <li><strong>Technical Data:</strong> Operating system, screen resolution, and connection type</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">4. How We Use Your Information</h2>
              <p className="mb-4">We process your personal data for the following purposes:</p>
              
              <h3 className="text-xl font-semibold text-white mb-3 mt-6">4.1 Service Delivery</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Responding to your enquiries and providing customer support</li>
                <li>Conducting free website audits and providing recommendations</li>
                <li>Delivering our web design and management services</li>
                <li>Processing payments and managing subscriptions</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">4.2 Communication</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Sending service updates and important notifications</li>
                <li>Responding to your questions and requests</li>
                <li>Providing technical support and assistance</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">4.3 Website Improvement</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Analysing website usage to improve user experience</li>
                <li>Identifying technical issues and optimising performance</li>
                <li>Understanding visitor behaviour and preferences</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">4.4 Legal Compliance</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Complying with legal obligations and regulations</li>
                <li>Protecting our rights and preventing fraud</li>
                <li>Enforcing our terms and conditions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">5. Legal Basis for Processing</h2>
              <p className="mb-4">We process your personal data based on the following legal grounds:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Consent:</strong> When you provide explicit consent (e.g., submitting contact forms)</li>
                <li><strong>Contract Performance:</strong> To fulfil our contractual obligations to you</li>
                <li><strong>Legitimate Interests:</strong> For business operations, fraud prevention, and service improvement</li>
                <li><strong>Legal Obligation:</strong> To comply with applicable laws and regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">6. Data Sharing and Disclosure</h2>
              <p className="mb-4">We do not sell your personal data. We may share your information with:</p>
              
              <h3 className="text-xl font-semibold text-white mb-3 mt-6">6.1 Service Providers</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Email service providers for communication</li>
                <li>Analytics platforms (e.g., Google Analytics) for website analysis</li>
                <li>Hosting providers for data storage</li>
                <li>Payment processors for transaction handling</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">6.2 Legal Requirements</h3>
              <p className="mb-4">We may disclose your information if required by law, court order, or government authority, or to protect our legal rights.</p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">7. Data Retention</h2>
              <p className="mb-4">We retain your personal data only for as long as necessary:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Contact Form Data:</strong> 2 years from last contact</li>
                <li><strong>Free Audit Data:</strong> 2 years from audit completion</li>
                <li><strong>Client Data:</strong> Duration of service plus 6 years for legal/tax purposes</li>
                <li><strong>Analytics Data:</strong> 26 months (Google Analytics default)</li>
                <li><strong>Chat Widget Data:</strong> 1 year from last interaction</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">8. Your Rights Under UK GDPR</h2>
              <p className="mb-4">You have the following rights regarding your personal data:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
                <li><strong>Right to Restriction:</strong> Limit how we use your data</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at team.marketme@gmail.com. We will respond within one month.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">9. Data Security</h2>
              <p className="mb-4">
                We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>SSL/TLS encryption for data transmission</li>
                <li>Secure servers and databases with access controls</li>
                <li>Regular security assessments and updates</li>
                <li>Staff training on data protection practices</li>
                <li>Secure backup and disaster recovery procedures</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">10. International Data Transfers</h2>
              <p>
                Your data is primarily stored and processed within the UK and European Economic Area (EEA). If we transfer data outside these regions, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses approved by the UK Information Commissioner's Office (ICO).
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">11. Children's Privacy</h2>
              <p>
                Our services are not directed to individuals under 16 years of age. We do not knowingly collect personal data from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">12. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any personal information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">13. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of significant changes by posting the updated policy on our website with a new "Last updated" date. Your continued use of our services after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">14. Complaints</h2>
              <p className="mb-4">
                If you have concerns about how we handle your personal data, please contact us first at team.marketme@gmail.com. We will investigate and respond promptly.
              </p>
              <p>
                You also have the right to lodge a complaint with the UK Information Commissioner's Office (ICO):
              </p>
              <p className="mt-4">
                <strong>Information Commissioner's Office</strong><br />
                Wycliffe House, Water Lane<br />
                Wilmslow, Cheshire SK9 5AF<br />
                Tel: 0303 123 1113<br />
                Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#8b2bd9] hover:underline">ico.org.uk</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">15. Contact Us</h2>
              <p className="mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <p>
                <strong>Email:</strong> team.marketme@gmail.com
              </p>
            </section>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}
